"use client";

import { useCallback, useState } from "react";
import { Message } from "@/lib/core/crm/types";
import {
  DirectMessageActions,
  type MessageActionOptions,
} from "@/lib/utils/crm/direct-message-actions";

/**
 * Simple hook that uses direct API calls for message actions
 * Bypasses all complex state management to ensure mutations actually work
 */
export function useDirectMessageActions() {
  const deleteMessage = useCallback(async (message: Message) => {
    console.log("🗑️ useDirectMessageActions: deleteMessage called for:", message.id);
    return DirectMessageActions.deleteMessage(message);
  }, []);

  const archiveMessage = useCallback(async (message: Message) => {
    console.log("📁 useDirectMessageActions: archiveMessage called for:", message.id);
    return DirectMessageActions.archiveMessage(message);
  }, []);

  const markAsRead = useCallback(
    async (message: Message, options?: MessageActionOptions) => {
      console.log("✅ useDirectMessageActions: markAsRead called for:", message.id);
      return DirectMessageActions.markAsRead(message, options);
    },
    []
  );

  const markAsUnread = useCallback(async (message: Message) => {
    console.log("📬 useDirectMessageActions: markAsUnread called for:", message.id);
    return DirectMessageActions.markAsUnread(message);
  }, []);

  const markImportant = useCallback(
    async (message: Message, options?: MessageActionOptions) => {
      console.log("⭐ useDirectMessageActions: markImportant called for:", message.id);
      return DirectMessageActions.markImportant(message, options);
    },
    []
  );

  const markSpam = useCallback(
    async (message: Message, options?: MessageActionOptions) => {
      console.log("🚫 useDirectMessageActions: markSpam called for:", message.id);
      return DirectMessageActions.markSpam(message, options);
    },
    []
  );

  /**
   * Real pending state, tracked here rather than reported as a constant.
   *
   * This used to be `isPending: false` with the comment "Direct calls don't have
   * loading state", which made every `disabled={isActionLoading}` in the inbox
   * dead code: nothing ever disabled and a double-click fired the action twice.
   *
   * A counter, not a boolean — an automatic mark-as-read (fired when a message
   * opens) genuinely overlaps a user-initiated archive, and a boolean would be
   * cleared by whichever settled first, unlocking the UI mid-flight.
   *
   * New code should prefer `useMailActions`, which speaks the typed
   * `MailActionRequest` contract and also handles bulk runs. This shim keeps the
   * existing call sites working while carrying the same guarantee.
   */
  const [pendingCount, setPendingCount] = useState(0);

  const tracked = useCallback(
    <TArgs extends unknown[], TResult>(
      operation: (...args: TArgs) => Promise<TResult>
    ): ((...args: TArgs) => Promise<TResult>) =>
      async (...args: TArgs) => {
        setPendingCount((n) => n + 1);
        try {
          return await operation(...args);
        } finally {
          // `finally` so a thrown action cannot leave the UI permanently locked.
          setPendingCount((n) => Math.max(0, n - 1));
        }
      },
    []
  );

  return {
    deleteMessage: tracked(deleteMessage),
    archiveMessage: tracked(archiveMessage),
    markAsRead: tracked(markAsRead),
    markAsUnread: tracked(markAsUnread),
    markImportant: tracked(markImportant),
    markSpam: tracked(markSpam),
    isPending: pendingCount > 0,
  };
}
